import { animated, useSpring } from "@react-spring/web";
import ExpandCollapseButton from "@src/straps/derived/ExpandCollapseButton/ExpandCollapseButton";
import classNames from "classnames";
import { useAtom } from "jotai";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { isLeftNavCollapsedAtom, useAutoExpandOnPathChange } from "./state";

const COLLAPSED_WIDTH = 70;
const EXPANDED_WIDTH = 260;
const AnimatedPanel: FunctionComponent<
  React.PropsWithChildren<{
    isCollapsed: boolean;
    isCollapsedModeEnabled: boolean;
  }>
> = ({ isCollapsed, isCollapsedModeEnabled, children }) => {
  const panelStyle = useSpring({
    width: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
  });
  const placeholderStyle = useSpring({
    width: isCollapsedModeEnabled ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
  });
  // The placeholder only tracks the collapsed mode, so an expanded nav in collapsed
  // mode is wider than the space reserved for it and floats over the page content.
  const isFloating = isCollapsedModeEnabled && !isCollapsed;
  return (
    <>
      <animated.div style={placeholderStyle}>{/* Placeholder */}</animated.div>
      <animated.nav
        className={classNames(
          "absolute left-0 top-0 z-10 h-full flex-shrink-0 bg-white",
          { "shadow-lg": isFloating }
        )}
        style={panelStyle}
      >
        {children}
      </animated.nav>
    </>
  );
};

export const LeftNav: FunctionComponent<React.PropsWithChildren> = ({
  children,
}) => {
  const [isCollapsedModeEnabled, setIsCollapsedModeEnabled] = useState(false);

  const [isCollapsed, setCollapsed] = useAtom(isLeftNavCollapsedAtom);
  const [isHovered, setIsHovered] = useState(false);
  const [isKeyboardFocused, setIsKeyboardFocused] = useState(false);
  useAutoExpandOnPathChange();

  // Use these to determine if focus was from key press
  const isKeyDown = useRef(false);
  const onKeyDown = useCallback(() => (isKeyDown.current = true), []);
  const onKeyUp = useCallback(() => (isKeyDown.current = false), []);

  const toggleCollapsedMode = useCallback(() => {
    const newMode = !isCollapsedModeEnabled;
    setIsCollapsedModeEnabled(newMode);
    setCollapsed(newMode);

    if (newMode) {
      setIsHovered(false);
      setIsKeyboardFocused(false);
    }
  }, [setIsCollapsedModeEnabled, isCollapsedModeEnabled, setCollapsed]);

  useEffect(() => {
    if (!isHovered && !isKeyboardFocused && isCollapsedModeEnabled) {
      setCollapsed(true);
    } else if (isCollapsed) {
      setCollapsed(false);
    }
  }, [
    isHovered,
    isKeyboardFocused,
    isCollapsed,
    setCollapsed,
    isCollapsedModeEnabled,
  ]);

  const expandCollapseNode = (
    <ExpandCollapseButton
      onClick={toggleCollapsedMode}
      isCollapsedModeEnabled={isCollapsedModeEnabled}
      top="2.25rem"
      right="-17px"
      zIndex="1"
      name="left navigation panel"
      dataTestId="left-nav-expand-collapse-button"
    />
  );

  return (
    <div className="relative z-50 h-full">
      <AnimatedPanel
        isCollapsed={isCollapsed}
        isCollapsedModeEnabled={isCollapsedModeEnabled}
      >
        {isCollapsed && expandCollapseNode}
        <div
          className="h-full overflow-y-auto"
          onMouseEnter={() => {
            setIsHovered(true);
          }}
          onMouseLeave={() => {
            setIsHovered(false);
          }}
          onFocus={(e) => {
            if (isKeyDown.current) {
              setIsKeyboardFocused(true);
            }
          }}
          onBlur={(e) => {
            const currentTarget = e.currentTarget;

            requestAnimationFrame(() => {
              if (!currentTarget.contains(document.activeElement)) {
                setIsKeyboardFocused(false);
              }
            });
          }}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          role="menu"
        >
          {!isCollapsed && expandCollapseNode}
          {children}
        </div>
      </AnimatedPanel>
    </div>
  );
};
